import { AsyncActionCreatorOptions } from '../types/Options';
import { AsyncActionMethods } from '../types/ActionMethods';
import { AsyncActionTypeValue } from '../actionTypes/types';
import { capitalize } from '../../lib/string/lib';
import { createAsyncActionTypeValue } from '../actionTypes/lib';
import { HttpVerb } from '@sonar-software/sonar-http';
import {
	AsyncActionCreatorDataValue,
	AsyncActionCreatorErrorOrFailValue,
	AsyncActionCreatorKey,
	AsyncActionCreatorNameOptions,
	AsyncActionCreatorOptionalDataValue,
	AsyncActionCreators,
	AsyncActionCreatorsMap,
	AsyncActionCreatorTypeValue,
	ErrorOrFail
} from './types';

export function createAsyncActionCreatorKey<AM extends AsyncActionMethods, HV extends HttpVerb, N extends string>(
	options: AsyncActionCreatorNameOptions<AM, HV, N>
): AsyncActionCreatorKey<AM, HV, N> {
	const { actionMethod, httpVerb, name } = options;
	const actionMethodLowercase = actionMethod.toLowerCase();
	const httpVerbCapialized = capitalize(httpVerb.toLowerCase());
	const nameCapitalized = capitalize(name);

	return `${actionMethodLowercase}${httpVerbCapialized}${nameCapitalized}` as AsyncActionTypeValue<AM, HV, N>;
}

export function createAsyncActionCreatorErrorOrFailValue<
	AM extends AsyncActionMethods,
	HV extends HttpVerb,
	N extends string
>(options: AsyncActionCreatorNameOptions<AM, HV, N>): AsyncActionCreatorErrorOrFailValue<AM, HV, N> {
	const actionTypeValue = createAsyncActionTypeValue<AM, HV, N>(options);

	return function(errorOrFail: ErrorOrFail) {
		const { statusCode } = errorOrFail;

		return {
			statusCode,
			type: actionTypeValue
		};
	};
}

export function createAsyncActionCreatorDataValue<
	AM extends AsyncActionMethods,
	HV extends HttpVerb,
	N extends string,
	T
>(options: AsyncActionCreatorNameOptions<AM, HV, N>): AsyncActionCreatorDataValue<AM, HV, N, T> {
	const actionTypeValue = createAsyncActionTypeValue<AM, HV, N>(options);

	return function(data: T) {
		return {
			data,
			type: actionTypeValue
		};
	};
}

export function createAsyncActionCreatorOptionalDataValue<
	AM extends AsyncActionMethods,
	HV extends HttpVerb,
	N extends string,
	T
>(options: AsyncActionCreatorNameOptions<AM, HV, N>): AsyncActionCreatorOptionalDataValue<AM, HV, N, T> {
	const actionTypeValue = createAsyncActionTypeValue<AM, HV, N>(options);

	return function(data?: T) {
		if (data === undefined) {
			return {
				type: actionTypeValue
			};
		}

		return {
			data,
			type: actionTypeValue
		};
	};
}

function createAsyncActionCreatorTypeValue<AM extends AsyncActionMethods, HV extends HttpVerb, N extends string>(
	options: AsyncActionCreatorNameOptions<AM, HV, N>
): AsyncActionCreatorTypeValue<AM, HV, N> {
	const actionTypeValue = createAsyncActionTypeValue<AM, HV, N>(options);

	return function() {
		return {
			type: actionTypeValue
		};
	};
}

export function createAsyncActionCreatorsMap<HV extends HttpVerb, N extends string, RQ = void, RC = void>(
	options: AsyncActionCreatorOptions<HV, N>,
	defaultRequest?: RQ
): AsyncActionCreatorsMap<HV, N, RQ, RC> {
	const { receive, request, ...rest } = options;

	const originalRequest =
		!request || request === 'optional' ?
			createAsyncActionCreatorOptionalDataValue<AsyncActionMethods.Request, HV, N, RQ>({
				actionMethod: AsyncActionMethods.Request,
				...rest
			  }) :
			createAsyncActionCreatorDataValue<AsyncActionMethods.Request, HV, N, RQ>({
				actionMethod: AsyncActionMethods.Request,
				...rest
			  });

	let lastRequest: RQ | undefined = defaultRequest;

	const wrappedRequest =
		!request || request === 'optional' ?
			(actionRequest?: RQ) => {
				lastRequest = actionRequest;

				return originalRequest(actionRequest);
			  } :
			(actionRequest: RQ) => {
				lastRequest = actionRequest;

				return originalRequest(actionRequest);
			  };

	const repeat = (transform?: (previous: RQ) => RQ) => {
		const previous = (lastRequest ?? defaultRequest) as RQ;

		return wrappedRequest(transform ? transform(previous) : previous);
	};

	return {
		[ AsyncActionMethods.Error ]: createAsyncActionCreatorErrorOrFailValue({
			actionMethod: AsyncActionMethods.Error,
			...rest
		}),
		[ AsyncActionMethods.Failed ]: createAsyncActionCreatorErrorOrFailValue({
			actionMethod: AsyncActionMethods.Failed,
			...rest
		}),
		[ AsyncActionMethods.Receive ]: receive ?
			createAsyncActionCreatorDataValue<AsyncActionMethods.Receive, HV, N, RC>({
				actionMethod: AsyncActionMethods.Receive,
				...options
			  }) :
			createAsyncActionCreatorTypeValue<AsyncActionMethods.Receive, HV, N>({
				actionMethod: AsyncActionMethods.Receive,
				...options
			  }),
		[ AsyncActionMethods.Repeat ]: repeat,
		[ AsyncActionMethods.Request ]: wrappedRequest,
		[ AsyncActionMethods.Reset ]: createAsyncActionCreatorTypeValue({
			actionMethod: AsyncActionMethods.Reset,
			...rest
		})
	} as AsyncActionCreatorsMap<HV, N, RQ, RC>;
}

export function createAsyncActionCreators<HV extends HttpVerb, N extends string, RQ = void, RC = void>(
	options: AsyncActionCreatorOptions<HV, N>,
	map: AsyncActionCreatorsMap<HV, N, RQ, RC>
): AsyncActionCreators<HV, N, AsyncActionCreatorsMap<HV, N, RQ, RC>> {
	const { receive, request, ...rest } = options;

	return {
		[ createAsyncActionCreatorKey({
			actionMethod: AsyncActionMethods.Error,
			...rest
		}) ]: map.error,
		[ createAsyncActionCreatorKey({
			actionMethod: AsyncActionMethods.Failed,
			...rest
		}) ]: map.failed,
		[ createAsyncActionCreatorKey({
			actionMethod: AsyncActionMethods.Receive,
			...rest
		}) ]: map.receive,
		[ createAsyncActionCreatorKey({
			actionMethod: AsyncActionMethods.Repeat,
			...rest
		}) ]: map.repeat,
		[ createAsyncActionCreatorKey({
			actionMethod: AsyncActionMethods.Request,
			...rest
		}) ]: map.request,
		[ createAsyncActionCreatorKey({
			actionMethod: AsyncActionMethods.Reset,
			...rest
		}) ]: map.reset
	} as AsyncActionCreators<HV, N, AsyncActionCreatorsMap<HV, N, RQ, RC>>;
}
